import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FormInput, Plus, GripVertical, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';

interface FormField {
  id: number;
  field_key: string;
  label: string;
  type: string;
  required: number;
  visible: number;
  protected: number;
  group_name: string;
}

const fieldTypes = [
  'text', 'number', 'date', 'select', 'multi_select', 'checkbox', 'textarea', 'file'
];

export default function FormBuilderPage() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [newField, setNewField] = useState({
    fieldKey: '',
    label: '',
    type: 'text',
    required: false,
    visible: true,
    groupName: 'general',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['form-fields'],
    queryFn: async () => api.get<any>('/api/forms/admission/fields'),
  });

  const fields: FormField[] = data?.data || [];

  const createMutation = useMutation({
    mutationFn: async (payload: any) => api.post('/api/forms/admission/fields', payload),
    onSuccess: () => {
      toast.success('Field added');
      setShowAddModal(false);
      setNewField({ fieldKey: '', label: '', type: 'text', required: false, visible: true, groupName: 'general' });
      queryClient.invalidateQueries({ queryKey: ['form-fields'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add field');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/forms/admission/fields/${id}`),
    onSuccess: () => {
      toast.success('Field deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['form-fields'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete field');
    },
  });

  const handleAddField = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(newField);
  };

  const groupedFields = fields.reduce((acc, field) => {
    const group = field.group_name || 'general';
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {} as Record<string, FormField[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <FormInput className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
            Form Builder
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Customize admission form fields</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="h-10 gap-2">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Field</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      {isLoading ? (
        <LoadingSkeleton count={4} cols="grid-cols-1" height="h-16" />
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFields).map(([group, groupFields]) => (
            <div key={group} className="glass-card rounded-2xl overflow-hidden">
              <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-light-border dark:border-dark-border bg-purple-50/50 dark:bg-purple-500/5">
                <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text capitalize">{group}</h3>
              </div>
              <div className="divide-y divide-light-border dark:divide-dark-border">
                {groupFields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <GripVertical className="w-4 h-4 text-light-text-muted cursor-grab" />
                      <div>
                        <p className="text-sm font-medium text-light-text dark:text-dark-text">{field.label}</p>
                        <p className="text-xs text-light-text-muted">{field.field_key} • {field.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {field.required === 1 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300">
                          Required
                        </span>
                      )}
                      {field.protected === 1 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300">
                          Protected
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={field.protected === 1}
                        onClick={() => { if (field.protected !== 1) setDeleteConfirm(field.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(groupedFields).length === 0 && (
            <div className="text-center py-12">
              <FormInput className="w-12 h-12 text-light-text-muted mx-auto mb-3" />
              <p className="text-sm text-light-text-muted">No form fields configured yet</p>
            </div>
          )}
        </div>
      )}

      {/* Add Field Modal */}
      {showAddModal && (
        <div className="fixed inset-1 bg-black/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="glass-modal p-4 sm:p-6 rounded-t-2xl sm:rounded-2xl w-full max-w-md bottom-sheet-modal"
          >
            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Add Field</h3>
            <form onSubmit={handleAddField} className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1">Field Key (internal name)</Label>
                <Input
                  type="text"
                  value={newField.fieldKey}
                  onChange={(e) => setNewField(prev => ({ ...prev, fieldKey: e.target.value }))}
                  placeholder="e.g., father_occupation"
                  required
                  className="h-10 w-full"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1">Label (display name)</Label>
                <Input
                  type="text"
                  value={newField.label}
                  onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g., Father Occupation"
                  required
                  className="h-10 w-full"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1">Field Type</Label>
                <Select value={newField.type} onValueChange={(v) => setNewField(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="required"
                  className="w-5 h-5 rounded border-purple-300"
                  checked={newField.required}
                  onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                />
                <Label htmlFor="required" className="text-sm text-light-text dark:text-dark-text">Required field</Label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="visible"
                  className="w-5 h-5 rounded border-purple-300"
                  checked={newField.visible}
                  onChange={(e) => setNewField(prev => ({ ...prev, visible: e.target.checked }))}
                />
                <Label htmlFor="visible" className="text-sm text-light-text dark:text-dark-text">Visible on form</Label>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => { setShowAddModal(false); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending} className="flex-1 h-10">
                  {createMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto" />
                  ) : (
                    'Add Field'
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Field"
        onConfirm={() => deleteMutation.mutate(deleteConfirm!)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
